import { parseStoredStage, type StoredStage } from "./stageRecord.js";
import { getStage, readStore, writeStage, type MemoryStorage } from "./stageStore.js";

type RemoteResult =
  | { kind: "ok"; value: unknown }
  | { kind: "missing" }
  | { kind: "unauthorized" }
  | { kind: "error"; message: string };

export class StageMakerUnauthorizedError extends Error {}

async function requestJson(url: string, init?: RequestInit): Promise<RemoteResult> {
  try {
    const response = await fetch(url, { ...init, credentials: "include" });
    if (response.status === 401) return { kind: "unauthorized" };
    if (response.status === 404) return { kind: "missing" };
    if (!response.ok) return { kind: "error", message: `server error (${response.status})` };
    try {
      return { kind: "ok", value: await response.json() };
    } catch {
      return { kind: "missing" };
    }
  } catch {
    return { kind: "missing" };
  }
}

function listedStages(value: unknown): Record<string, StoredStage> | null {
  if (!value || typeof value !== "object" || !Array.isArray((value as { stages?: unknown }).stages)) return null;
  const parsed = (value as { stages: unknown[] }).stages.map(parseStoredStage);
  if (parsed.some((stage) => stage === null)) return null;
  return Object.fromEntries((parsed as StoredStage[]).map((stage) => [stage.id, stage]));
}

export async function loadAllStages(storage: MemoryStorage): Promise<Record<string, StoredStage>> {
  const remote = await requestJson("/api/stage-maker/custom-stages");
  if (remote.kind === "unauthorized") throw new StageMakerUnauthorizedError("unauthorized");
  if (remote.kind === "error") throw new Error(remote.message);
  const listed = remote.kind === "ok" ? listedStages(remote.value) : null;
  if (listed) {
    storage.setItem("stage-maker.v1", JSON.stringify(listed));
    return listed;
  }
  return readStore(storage);
}

export async function loadPublishedStages(storage: MemoryStorage): Promise<Record<string, StoredStage>> {
  const remote = await requestJson("/api/custom-stages");
  const listed = remote.kind === "ok" ? listedStages(remote.value) : null;
  if (listed) {
    storage.setItem("stage-maker.v1", JSON.stringify({ ...readStore(storage), ...listed }));
  }
  const stages = listed ?? readStore(storage);
  return Object.fromEntries(Object.entries(stages).filter(([, stage]) => stage.status === "published"));
}

export async function loadOneStage(id: string, storage: MemoryStorage): Promise<StoredStage | null> {
  const remote = await requestJson(`/api/stage-maker/custom-stages/${encodeURIComponent(id)}`);
  if (remote.kind === "unauthorized") throw new StageMakerUnauthorizedError("unauthorized");
  if (remote.kind === "error") throw new Error(remote.message);
  const candidate = remote.kind === "ok" && remote.value && typeof remote.value === "object"
    ? (remote.value as { stage?: unknown }).stage
    : undefined;
  const stage = parseStoredStage(candidate);
  if (stage) {
    writeStage(storage, stage);
    return stage;
  }
  return getStage(storage, id) ?? null;
}

export async function saveStage(stage: StoredStage, storage: MemoryStorage): Promise<void> {
  writeStage(storage, stage);
  const remote = await requestJson(`/api/stage-maker/custom-stages/${encodeURIComponent(stage.id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(stage),
  });
  if (remote.kind === "unauthorized") throw new StageMakerUnauthorizedError("unauthorized");
  if (remote.kind === "error") throw new Error(remote.message);
  // missing はAPIのないローカル開発なので、端末保存だけ残す。
}
